// ─── Template: badge-stamp-v1 ──────────────────────────────────────────────
// A circular or pill-shaped badge that bounces in with a slight rotation
// and a subtle shine sweep. Use for "NUEVO", "SALE", "ENVÍO GRATIS",
// promo stamps, etc.

import React from 'react';
import { AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AnimationTemplate } from '../types';

export type AlignPreset = 'left' | 'center' | 'right';
export type BadgeShape = 'circle' | 'pill' | 'square';
const FONT_STACK = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export type BadgeStampProps = {
  text: string;
  textFont: string;
  textSize: number;
  textColor: string;
  textBold: boolean;
  textItalic: boolean;
  textAlign: AlignPreset;
  /** Badge shape. Pill auto-sizes to text length. */
  shape: BadgeShape;
  /** Badge fill color. */
  badgeColor: string;
  /** Optional border color (set to transparent for none). */
  borderColor: string;
  borderWidth: number;
  /** Rotation in degrees applied to the whole badge. */
  rotation: number;
  /** Size of the badge as % of composition height. */
  badgeSize: number;
};

const Component: React.FC<BadgeStampProps> = ({
  text, textFont, textSize, textColor, textBold, textItalic, textAlign,
  shape, badgeColor, borderColor, borderWidth, rotation, badgeSize,
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // Bouncy entry: scale overshoots slightly then settles.
  const scale = interpolate(frame, [0, 14, 22], [0, 1.15, 1], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });
  const wobble = interpolate(frame, [22, 36], [0, 1], {
    extrapolateRight: 'clamp',
  });
  const wobbleRotation = Math.sin(frame * 0.18) * 2 * (1 - wobble);

  // Shine sweep across the badge after settle.
  const shineX = interpolate(frame, [24, 42], [-1, 1.6], {
    easing: Easing.linear,
    extrapolateRight: 'clamp',
  });

  // Tamaños sobre el lado más corto — `height` solo hacía que el sello
  // se viera enorme en formato vertical (9:16).
  const refDim = Math.min(width, height);
  const sizePx = (badgeSize / 100) * refDim;
  const textPx = (textSize / 100) * refDim;
  const padding = sizePx * 0.18;

  // Pill width grows with text length so long words still breathe.
  const pillWidth = shape === 'pill'
    ? Math.max(sizePx, textPx * Math.max(text.length, 4) * 0.62 + padding * 2)
    : sizePx;
  const pillHeight = shape === 'circle' || shape === 'square' ? sizePx : sizePx * 0.55;

  const radius =
    shape === 'circle' ? '50%' :
    shape === 'pill'   ? `${pillHeight / 2}px` :
    `${sizePx * 0.12}px`;

  return (
    <AbsoluteFill style={{ background: 'transparent', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_STACK }}>
      <div
        style={{
          width: pillWidth,
          height: pillHeight,
          background: badgeColor,
          borderRadius: radius,
          border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor}` : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: `0 ${padding}px`,
          transform: `scale(${scale}) rotate(${rotation + wobbleRotation}deg)`,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            color: textColor,
            fontFamily: `${textFont}, ${FONT_STACK}`,
            fontSize: textPx,
            fontWeight: textBold ? 800 : 600,
            fontStyle: textItalic ? 'italic' : 'normal',
            textAlign: textAlign,
            letterSpacing: 1,
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            position: 'relative',
            zIndex: 2,
          }}
        >
          {text}
        </span>
        {/* Shine sweep — diagonal white gradient that crosses the badge once. */}
        <div
          style={{
            position: 'absolute',
            top: 0, bottom: 0,
            left: `${shineX * 100}%`,
            width: '40%',
            background: 'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />
      </div>
      {/* Avoid unused-var warnings — width is referenced for future composition tweaks. */}
      <div style={{ display: 'none' }}>{width}</div>
    </AbsoluteFill>
  );
};

const ALIGN_OPTIONS = [
  { value: 'left',   label: 'Izquierda' },
  { value: 'center', label: 'Centro' },
  { value: 'right',  label: 'Derecha' },
];
const SHAPE_OPTIONS = [
  { value: 'circle', label: 'Círculo' },
  { value: 'pill',   label: 'Cápsula' },
  { value: 'square', label: 'Cuadrado' },
];

export const badgeStampTemplate: AnimationTemplate<BadgeStampProps> = {
  id: 'badge-stamp-v1',
  label: 'Sello',
  description: 'Sello con texto que aparece con rebote y un brillo que cruza.',
  category: 'text',
  defaultDurationSec: 2.5,
  defaultProps: {
    text: 'NUEVO',
    textFont: 'Inter',
    textSize: 4.5,
    textColor: '#FFFFFF',
    textBold: true,
    textItalic: false,
    textAlign: 'center',
    shape: 'pill',
    badgeColor: '#DC2626',
    borderColor: '#FFFFFF',
    borderWidth: 0,
    rotation: -8,
    badgeSize: 24,
  },
  propsSchema: {
    text: {
      type: 'text', label: 'Texto', placeholder: 'NUEVO', agentExposed: true,
      styling: {
        font: 'textFont', size: 'textSize', color: 'textColor',
        bold: 'textBold', italic: 'textItalic', align: 'textAlign',
      },
    },
    textFont:   { type: 'font',    label: 'Tipografía' },
    textSize:   { type: 'number',  label: 'Tamaño', min: 2, max: 10, step: 0.2, decimals: 1, unit: '%' },
    textColor:  { type: 'color',   label: 'Color' },
    textBold:   { type: 'boolean', label: 'Negrita' },
    textItalic: { type: 'boolean', label: 'Cursiva' },
    textAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    shape:       { type: 'select', label: 'Forma', options: SHAPE_OPTIONS },
    badgeColor:  { type: 'color',  label: 'Color del badge' },
    borderColor: { type: 'color',  label: 'Color del borde' },
    borderWidth: { type: 'number', label: 'Grosor del borde', min: 0, max: 12, step: 1, decimals: 0, unit: 'px' },
    rotation:    { type: 'number', label: 'Rotación', min: -45, max: 45, step: 1, decimals: 0, unit: '°' },
    badgeSize:   { type: 'number', label: 'Tamaño del badge', min: 8, max: 60, step: 1, decimals: 0, unit: '%' },
  },
  Component,
};
